import { ComponentType, PropsWithChildren, type ReactElement, type ReactNode } from 'react';
import { Add } from '@/components/icons';
import { PageHeader, PageShell, type BreadcrumbItem } from '@/components/system';
import { Button } from '@/components/ui/button';
import type { PageShellWidth } from '@/components/system/PageShell';

type HeaderToolbar = {
    search?: ReactNode;
    filters?: ReactNode;
    controls?: ReactNode;
};

type Props = {
    title?: string;
    subtitle?: string;
    showHeader?: boolean;
    width?: PageShellWidth;
    leftIcon?: ComponentType<{ className?: string }>;

    // Slotted header API (forwarded to PageHeader).
    breadcrumb?: BreadcrumbItem[];
    backTo?: string;
    onBack?: () => void;
    meta?: ReactNode;
    actions?: ReactNode;
    overflow?: ReactNode;
    tabs?: ReactNode;
    toolbar?: HeaderToolbar;
    variant?: 'bar' | 'workbench';
    sticky?: boolean;

    // Legacy convenience action (kept for un-migrated callers).
    actionButton?: boolean;
    actionButtonText?: string;
    onClick?: () => void;
} & PropsWithChildren;

function WrapperLayout({
    title,
    subtitle,
    showHeader = true,
    width,
    leftIcon,
    breadcrumb,
    backTo,
    onBack,
    meta,
    actions,
    overflow,
    tabs,
    toolbar,
    variant,
    sticky,
    actionButton,
    actionButtonText,
    onClick,
    children,
}: Props): ReactElement {
    const legacyAction = actionButton ? (
        <Button onClick={onClick} size="lg" className="w-full justify-center sm:w-auto">
            <Add className="size-4" />
            {actionButtonText}
        </Button>
    ) : null;

    return (
        <PageShell width={width}>
            {showHeader && (
                <PageHeader
                    title={title}
                    subtitle={subtitle}
                    icon={leftIcon}
                    breadcrumb={breadcrumb}
                    backTo={backTo}
                    onBack={onBack}
                    meta={meta}
                    actions={actions ?? legacyAction}
                    overflow={overflow}
                    tabs={tabs}
                    toolbar={toolbar}
                    variant={variant}
                    sticky={sticky}
                />
            )}
            <div className="space-y-6">{children}</div>
        </PageShell>
    );
}

export default WrapperLayout;
